import { useRef, useState, useCallback, useEffect } from 'react';
import { Dropdown, Typography, Spin, Empty, Divider } from 'antd';
import {
  PaperClipOutlined, FileOutlined, FolderOpenOutlined,
  QuestionCircleOutlined, OrderedListOutlined, ThunderboltOutlined,
  ApartmentOutlined, RobotOutlined, PlusOutlined, CheckOutlined,
} from '@ant-design/icons';
import { useChatStore, ChatMode, AttachedSkill, AttachedWorkflow } from '@/stores/chat';
import { skillsAPI, workflowsAPI } from '@/services/api';

const { Text } = Typography;

interface AttachMenuProps {
  /** 选择文件/文件夹后回调（File 数组） */
  onFilesSelected: (files: File[]) => void;
  /** 打开「创建智能体」弹窗 */
  onOpenCreateAgent?: () => void;
}

const MODE_ITEMS: { value: ChatMode; label: string; desc: string; icon: React.ReactNode; color: string }[] = [
  { value: 'qa', label: '问答模式', desc: '一问一答，获取精准答案', icon: <QuestionCircleOutlined />, color: '#6366f1' },
  { value: 'plan', label: '计划模式', desc: 'AI 先规划步骤，再逐步执行', icon: <OrderedListOutlined />, color: '#f59e0b' },
  { value: 'execute', label: '完全执行权限', desc: '跳过规划，直接操作工具执行', icon: <ThunderboltOutlined />, color: '#10b981' },
];

interface SkillRow {
  id: string;
  name: string;
  description?: string;
  coreMission?: string;
  criticalRules?: string[];
}

/**
 * Codex 风格附件菜单：上传文件/文件夹、对话模式、技能、智能体、智能工作流
 * 由 Composer 的「📎」按钮触发，选择后以 Chip 形式展示在输入区。
 */
export default function AttachMenu({ onFilesSelected, onOpenCreateAgent }: AttachMenuProps) {
  const {
    mode, setMode, sessions, switchSession,
    attachedSkills, attachSkill, removeAttachedSkill,
    attachedWorkflow, setAttachedWorkflow,
  } = useChatStore();

  const [open, setOpen] = useState(false);
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [workflows, setWorkflows] = useState<AttachedWorkflow[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [loadingWf, setLoadingWf] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const myAgents = sessions.filter((s) => !!s.systemPrompt && s.systemPrompt.trim().length > 0);

  // 打开菜单时懒加载技能与公开工作流
  const loadData = useCallback(async () => {
    if (skills.length === 0) {
      setLoadingSkills(true);
      try {
        const res: any = await skillsAPI.list();
        const list: SkillRow[] = (res?.skills || res?.data?.skills || []).map((s: any) => ({
          id: s.id || s.manifest?.id,
          name: s.name || s.manifest?.name,
          description: s.description || s.manifest?.description,
          coreMission: s.coreMission || s.manifest?.coreMission,
          criticalRules: s.criticalRules || s.manifest?.criticalRules,
        })).filter((s: SkillRow) => s.id && s.name);
        setSkills(list);
      } catch { /* 忽略 */ } finally { setLoadingSkills(false); }
    }
    if (workflows.length === 0) {
      setLoadingWf(true);
      try {
        const res: any = await workflowsAPI.listPublic();
        const list: AttachedWorkflow[] = (res?.data || []).map((w: any) => ({
          id: String(w._id || w.id),
          name: w.name,
          description: w.description,
        })).filter((w: AttachedWorkflow) => w.id && w.name);
        setWorkflows(list.slice(0, 12));
      } catch { /* 忽略 */ } finally { setLoadingWf(false); }
    }
  }, [skills.length, workflows.length]);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onFilesSelected(files);
    e.target.value = '';
  };

  const handleFolderClick = () => {
    const input = folderInputRef.current;
    if (!input) return;
    input.setAttribute('webkitdirectory', '');
    input.setAttribute('directory', '');
    input.setAttribute('multiple', '');
    input.click();
  };

  const triggerNode = (
    <span className="attach-trigger">
      <PaperClipOutlined />
    </span>
  );

  return (
    <>
      {/* 隐藏输入：文件 / 文件夹 */}
      <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileInput} />
      <input ref={folderInputRef} type="file" multiple hidden onChange={handleFileInput} />

      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={['click']}
        placement="topLeft"
        dropdownRender={() => (
          <div className="attach-menu" onClick={(e) => e.stopPropagation()}>
            {/* 上传 */}
            <div className="attach-section">
              <Text className="attach-label">上传</Text>
              <div className="attach-row" onClick={() => fileInputRef.current?.click()}>
                <FileOutlined className="attach-icon" /> 上传文件
                <Text className="attach-hint">PDF/图片/代码 ≤50MB</Text>
              </div>
              <div className="attach-row" onClick={handleFolderClick}>
                <FolderOpenOutlined className="attach-icon" /> 上传文件夹
                <Text className="attach-hint">批量导入目录内文件</Text>
              </div>
            </div>

            <Divider style={{ margin: '6px 0' }} />

            {/* 对话模式 */}
            <div className="attach-section">
              <Text className="attach-label">对话模式</Text>
              {MODE_ITEMS.map((m) => (
                <div
                  key={m.value}
                  className={`attach-row ${mode === m.value ? 'selected' : ''}`}
                  onClick={() => { setMode(m.value); setOpen(false); }}
                >
                  <span className="attach-icon" style={{ color: m.color }}>{m.icon}</span>
                  {m.label}
                  <Text className="attach-hint">{m.desc}</Text>
                  {mode === m.value && <CheckOutlined style={{ color: '#6366f1', marginLeft: 'auto' }} />}
                </div>
              ))}
            </div>

            <Divider style={{ margin: '6px 0' }} />

            {/* 技能 */}
            <div className="attach-section">
              <Text className="attach-label">技能 / 插件</Text>
              {loadingSkills ? (
                <div className="attach-loading"><Spin size="small" /> 加载技能中...</div>
              ) : skills.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无技能" style={{ margin: '8px 0' }} />
              ) : (
                <div className="attach-scroll">
                  {skills.map((s) => {
                    const active = attachedSkills.some((x) => x.id === s.id);
                    return (
                      <div
                        key={s.id}
                        className={`attach-row ${active ? 'selected' : ''}`}
                        onClick={() => {
                          if (active) removeAttachedSkill(s.id);
                          else attachSkill({ id: s.id, name: s.name, coreMission: s.coreMission, criticalRules: s.criticalRules });
                        }}
                      >
                        <ApartmentOutlined className="attach-icon" />
                        {s.name}
                        <Text className="attach-hint">{s.description || s.coreMission || ''}</Text>
                        {active && <CheckOutlined style={{ color: '#6366f1', marginLeft: 'auto' }} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Divider style={{ margin: '6px 0' }} />

            {/* 智能体 */}
            <div className="attach-section">
              <Text className="attach-label">智能体</Text>
              {myAgents.length > 0 && (
                <div className="attach-scroll attach-scroll-sm">
                  {myAgents.slice(0, 6).map((ag) => (
                    <div
                      key={ag.id}
                      className="attach-row"
                      onClick={() => { switchSession(ag.id); setOpen(false); }}
                    >
                      <RobotOutlined className="attach-icon" />
                      {ag.title}
                      <Text className="attach-hint">{ag.description || '自定义智能体'}</Text>
                    </div>
                  ))}
                </div>
              )}
              <div className="attach-row" onClick={() => { setOpen(false); onOpenCreateAgent?.(); }}>
                <PlusOutlined className="attach-icon" /> 创建智能体
                <Text className="attach-hint">设置系统提示词/工作流</Text>
              </div>
            </div>

            <Divider style={{ margin: '6px 0' }} />

            {/* 智能工作流 */}
            <div className="attach-section">
              <Text className="attach-label">智能工作流（本站项目）</Text>
              {loadingWf ? (
                <div className="attach-loading"><Spin size="small" /> 加载工作流中...</div>
              ) : workflows.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无公开工作流" style={{ margin: '8px 0' }} />
              ) : (
                <div className="attach-scroll attach-scroll-sm">
                  {workflows.map((w) => {
                    const active = attachedWorkflow?.id === w.id;
                    return (
                      <div
                        key={w.id}
                        className={`attach-row ${active ? 'selected' : ''}`}
                        onClick={() => { setAttachedWorkflow(active ? null : w); setOpen(false); }}
                      >
                        <ThunderboltOutlined className="attach-icon" />
                        {w.name}
                        <Text className="attach-hint">{w.description || ''}</Text>
                        {active && <CheckOutlined style={{ color: '#6366f1', marginLeft: 'auto' }} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      >
        {triggerNode}
      </Dropdown>

      <style>{`
        .attach-trigger { display: inline-flex; align-items: center; padding: 0 4px; font-size: 15px; }
        .attach-menu {
          width: 320px; max-height: 520px; overflow-y: auto;
          background: var(--bg-container); border: 1px solid var(--border);
          border-radius: 12px; padding: 8px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.18);
        }
        .attach-section { display: flex; flex-direction: column; gap: 2px; }
        .attach-label {
          font-size: 11px; color: var(--text-tertiary); text-transform: uppercase;
          letter-spacing: 1px; padding: 4px 8px 2px; display: block;
        }
        .attach-row {
          display: flex; align-items: center; gap: 8px;
          padding: 7px 8px; border-radius: 8px; cursor: pointer;
          font-size: 13px; color: var(--text-primary); transition: background 0.15s;
          border: 1px solid transparent;
        }
        .attach-row:hover { background: var(--bg-sidebar-hover); }
        .attach-row.selected { background: rgba(99, 102, 241, 0.10); border-color: rgba(99, 102, 241, 0.25); }
        .attach-icon { color: var(--text-secondary); font-size: 14px; flex-shrink: 0; }
        .attach-hint {
          font-size: 11px; color: var(--text-tertiary); margin-left: auto;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 130px;
        }
        .attach-scroll { max-height: 180px; overflow-y: auto; }
        .attach-scroll-sm { max-height: 140px; }
        .attach-loading { display: flex; align-items: center; gap: 8px; padding: 8px; color: var(--text-tertiary); font-size: 12px; }
        @media (max-width: 768px) { .attach-menu { width: 280px; } }
      `}</style>
    </>
  );
}
